/**
 * @fileoverview Shared display formatting for the timesheets surfaces.
 *
 * One place for the unit → hours conversion and AUD formatting used by the
 * weekly timesheet, approvals queue, reports and REM pack, so every surface
 * renders the same numbers and periods the same way.
 */

import { format, parseISO } from "date-fns";

/** Convert billing units to decimal hours. */
export function unitsToHours(units: number, unitMinutes: number): number {
  return (units * unitMinutes) / 60;
}

/** "7.5h" — hours label from units. */
export function formatUnitsAsHours(units: number, unitMinutes: number): string {
  return `${unitsToHours(units, unitMinutes).toLocaleString("en-AU", {
    maximumFractionDigits: 2,
  })}h`;
}

/** "$1,234" (whole dollars — dashboards and ledgers). */
export function formatAud(value: number): string {
  return value.toLocaleString("en-AU", {
    style: "currency",
    currency: "AUD",
    maximumFractionDigits: 0,
  });
}

/** "$1,234.50" (cents — per-entry and REM pack amounts). */
export function formatAudExact(value: number): string {
  return value.toLocaleString("en-AU", {
    style: "currency",
    currency: "AUD",
    maximumFractionDigits: 2,
  });
}

/**
 * "24 Aug – 30 Aug 2026" from two ISO dates — the year is stated once unless
 * the range crosses one, in which case both ends carry it.
 */
export function formatDateRange(start: string, end: string): string {
  const from = parseISO(start);
  const to = parseISO(end);
  const sameYear = from.getFullYear() === to.getFullYear();
  return `${format(from, sameYear ? "d MMM" : "d MMM yyyy")} – ${format(to, "d MMM yyyy")}`;
}
